import { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Trash2, Plus, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function AdditionalCostsSection({ projectId, readOnly, costs, reload }) {
  const [form, setForm] = useState({ label: "", amount: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ label: "", amount: "" });
  const items = costs || [];
  const load = reload || (() => {});

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${projectId}/additional-costs`, {
        label: form.label, amount: parseFloat(form.amount) || 0,
      });
      setForm({ label: "", amount: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditForm({ label: c.label || "", amount: c.amount ?? "" }); };
  const cancelEdit = () => { setEditingId(null); setEditForm({ label: "", amount: "" }); };

  const saveEdit = async (id) => {
    try {
      await api.put(`/additional-costs/${id}`, { label: editForm.label, amount: parseFloat(editForm.amount) || 0 });
      cancelEdit();
      load();
      toast.success("Cost updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/additional-costs/${id}`); load(); };

  const total = items.reduce((s, c) => s + (c.amount || 0), 0);
  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const inputCls = "h-9 rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]";

  return (
    <div className="max-w-4xl space-y-6 pt-10 mt-10 border-t border-[#EBE5E0]" data-testid="additional-costs-section">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Other</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Additional Costs</h3>
          <p className="text-sm text-[#5C5651] mt-1">Fees, delivery, labor and other charges — included in total spent.</p>
        </div>
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">Total Additional</p>
          <p data-testid="additional-costs-total" className="font-serif text-3xl text-[#2B2622]">{fmt(total)}</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-10 text-center text-[#5C5651]">
          No additional costs recorded yet.
        </div>
      ) : (
        <div className="border border-[#EBE5E0] bg-white">
          <table className="w-full text-sm">
            <tbody>
              {items.map((c) => (
                editingId === c.id ? (
                  <tr key={c.id} className="border-b border-[#F2EFEB] last:border-0 bg-[#FBFAF8]" data-testid="additional-cost-row-editing">
                    <td className="px-5 py-3">
                      <Input data-testid={`edit-cost-label-${c.id}`} value={editForm.label}
                        onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} className={inputCls} />
                    </td>
                    <td className="px-5 py-3 w-40">
                      <Input data-testid={`edit-cost-amount-${c.id}`} type="number" step="0.01" value={editForm.amount}
                        onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                        className={`${inputCls} text-right`} />
                    </td>
                    <td className="px-5 py-3 text-right w-24 whitespace-nowrap">
                      <button data-testid={`save-cost-${c.id}`} onClick={() => saveEdit(c.id)}
                        className="text-[#506B52] hover:text-[#3d523f] mr-3"><Check size={17} strokeWidth={1.75} /></button>
                      <button data-testid={`cancel-cost-${c.id}`} onClick={cancelEdit}
                        className="text-[#5C5651] hover:text-[#9E4747]"><X size={17} strokeWidth={1.75} /></button>
                    </td>
                  </tr>
                ) : (
                  <tr key={c.id} className="border-b border-[#F2EFEB] last:border-0" data-testid="additional-cost-row">
                    <td className="px-5 py-3 text-[#2B2622]">{c.label}</td>
                    <td className="px-5 py-3 text-right text-[#2B2622] w-40">{fmt(c.amount)}</td>
                    {!readOnly && (
                      <td className="px-5 py-3 text-right w-24 whitespace-nowrap">
                        <button data-testid={`edit-cost-${c.id}`} onClick={() => startEdit(c)}
                          className="text-[#5C5651] hover:text-[#A68A6B] mr-3"><Pencil size={15} strokeWidth={1.5} /></button>
                        <button data-testid={`delete-cost-${c.id}`} onClick={() => remove(c.id)}
                          className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} strokeWidth={1.5} /></button>
                      </td>
                    )}
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!readOnly && (
        <form onSubmit={add} className="grid sm:grid-cols-3 gap-3 items-end border border-[#EBE5E0] bg-[#F9F8F6] p-4">
          <Input data-testid="cost-label-input" placeholder="Description (e.g., Delivery fee)" required value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
          <Input data-testid="cost-amount-input" type="number" step="0.01" placeholder="Amount" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            className="rounded-none border-[#EBE5E0] bg-white focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
          <Button data-testid="add-cost-btn" type="submit"
            className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs">
            <Plus size={15} className="mr-1" /> Add Cost
          </Button>
        </form>
      )}
    </div>
  );
}
